/**
 * Optimized Meta Pixel Catalog Data Service
 *
 * Enhanced for automatic catalog detection and registration with Meta Pixel.
 * Optimizations include:
 * - Consolidated helper functions
 * - Intelligent caching
 * - Batch operations
 * - Memory optimization
 * - Complete Meta Conversions API integration
 * - Progressive loading
 * - Backend pre-computed metaPixelCatalogData integration (NEW)
 *
 * PERFORMANCE OPTIMIZATION:
 * This service now prioritizes pre-computed metaPixelCatalogData from the backend
 * which eliminates expensive frontend calculations and provides consistent,
 * optimized catalog data for Google Rich Snippets and Meta Pixel tracking.
 */

// Global interfaces for tracking
declare global {
  interface Window {
    dataLayer: any[];
    fbq?: (command: string, eventName: string, parameters?: any) => void;
  }
}

// Product interface for catalog tracking - Optimized with proper typing
interface CatalogProduct {
  id: string | number;
  sku: string;
  name: string;
  category?: string;
  brand?: string;
  price?: {
    regPrice?: number;
    discount?: {
      newPrice?: number;
      discountPercentage?: number;
    } | null;
  };
  // Legacy fields for backward compatibility
  discountPrice?: number;
  priceValue?: number;
  stock?: number;
  images?: Array<{ url?: string; medium?: string; small?: string }>;
  partner?: { name?: string };
  url?: string;
  description?: string;
  attributes?: Array<{ name: string; value: string }>;
  customizableOptions?: any[];
  // Freebie support
  isFreebie?: boolean;
  seo?: {
    structuredData?:
      | string
      | {
          mpn?: string;
          brand?: { name?: string };
          category?: string;
          offers?: {
            priceCurrency?: string;
            price?: string;
          };
        };
  };
  product_categories?: Array<{ name: string; url?: string }>;
}

// Enhanced catalog data structure - Optimized for Meta's automatic detection
interface EnhancedCatalogData {
  // Core Facebook Pixel required fields
  content_ids: string[];
  content_type: "product" | "product_group";
  content_name: string;
  content_category: string;
  value: number;
  currency: string;

  // Enhanced catalog fields for automatic detection
  brand?: string;
  retailer_item_id?: string;
  availability: "in stock" | "out of stock" | "preorder" | "backorder";
  condition: "new" | "used" | "refurbished";
  image_url?: string;
  product_url?: string;
  description?: string;

  // Pricing and inventory
  inventory?: number;
  sale_price?: number;
  regular_price?: number;
  discount_percentage?: number;

  // Product classification
  product_type?: string;
  google_product_category?: string;
  custom_labels?: string[];

  // Product identifiers for catalog matching
  gtin?: string;
  mpn?: string;

  // Behavioral and session context
  search_string?: string;
  num_items?: number;
  predicted_ltv?: number;
  session_id?: string;
  user_data?: {
    source: "organic" | "paid" | "email" | "social" | "direct";
    device_type: "mobile" | "tablet" | "desktop";
    is_returning_visitor: boolean;
  };
}

// Cache interface for performance optimization
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

// Product data transformation cache
class ProductDataCache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  clear(): void {
    this.cache.clear();
  }

  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

// Consolidated product data helper class
class ProductDataHelper {
  // Cache for expensive operations
  private static cache = new ProductDataCache();

  /**
   * Get product price with caching
   */
  static getProductPrice(product: CatalogProduct): number {
    const cacheKey = `price_${product.sku}`;
    const cached = this.cache.get<number>(cacheKey);
    if (cached !== null) return cached;

    let price = 0;

    // Handle new price structure
    if (product.price?.regPrice) {
      price = product.price.regPrice;
    }
    // Handle legacy price field
    else if (typeof product.priceValue === "number") {
      price = product.priceValue;
    }
    // Handle direct price field for backward compatibility
    else if (typeof (product as any).price === "number") {
      price = (product as any).price;
    }

    this.cache.set(cacheKey, price);
    return price;
  }

  /**
   * Get discount price with caching
   */
  static getDiscountPrice(product: CatalogProduct): number | null {
    const cacheKey = `discount_${product.sku}`;
    const cached = this.cache.get<number | null>(cacheKey);
    if (cached !== null) return cached;

    let discountPrice: number | null = null;

    // Handle new discount structure
    if (product.price?.discount?.newPrice) {
      discountPrice = product.price.discount.newPrice;
    }
    // Handle legacy discount field
    else if (product.discountPrice) {
      discountPrice = product.discountPrice;
    }

    this.cache.set(cacheKey, discountPrice);
    return discountPrice;
  }

  /**
   * Get brand with fallback logic
   */
  static getBrand(product: CatalogProduct): string {
    const cacheKey = `brand_${product.sku}`;
    const cached = this.cache.get<string>(cacheKey);
    if (cached) return cached;

    let brand = "Evefant"; // Default brand

    // Try structured data first
    if (product.seo?.structuredData) {
      try {
        const structuredData =
          typeof product.seo.structuredData === "string"
            ? JSON.parse(product.seo.structuredData)
            : product.seo.structuredData;

        if (structuredData.brand?.name) {
          brand = structuredData.brand.name;
        }
      } catch (e) {
        console.warn("Error parsing structured data for brand:", e);
      }
    }

    // Fallback to partner name
    if (brand === "Evefant" && product.partner?.name) {
      brand = product.partner.name;
    }

    // Fallback to direct brand field
    if (brand === "Evefant" && product.brand) {
      brand = product.brand;
    }

    this.cache.set(cacheKey, brand);
    return brand;
  }

  /**
   * Get category with fallback logic
   */
  static getCategory(product: CatalogProduct): string {
    const cacheKey = `category_${product.sku}`;
    const cached = this.cache.get<string>(cacheKey);
    if (cached) return cached;

    let category = "General";

    // Try product categories first
    if (product.product_categories?.[0]?.name) {
      category = product.product_categories[0].name;
    }
    // Try structured data
    else if (product.seo?.structuredData) {
      try {
        const structuredData =
          typeof product.seo.structuredData === "string"
            ? JSON.parse(product.seo.structuredData)
            : product.seo.structuredData;

        if (structuredData.category) {
          category = structuredData.category;
        }
      } catch (e) {
        console.warn("Error parsing structured data for category:", e);
      }
    }
    // Fallback to direct category field
    else if (product.category) {
      category = product.category;
    }

    this.cache.set(cacheKey, category);
    return category;
  }

  /**
   * Get MPN (Manufacturer Part Number) from structured data
   */
  static getMPN(product: CatalogProduct): string | undefined {
    if (product.seo?.structuredData) {
      try {
        const structuredData =
          typeof product.seo.structuredData === "string"
            ? JSON.parse(product.seo.structuredData)
            : product.seo.structuredData;

        return structuredData.mpn;
      } catch (e) {
        console.warn("Error parsing structured data for MPN:", e);
      }
    }
    return undefined;
  }

  /**
   * Get absolute product URL
   */
  static getProductURL(product: CatalogProduct): string {
    const baseUrl = "https://evefant.com";
    return `${baseUrl}/p/${product.url || product.sku}`;
  }

  /**
   * Get absolute image URL
   */
  static getImageURL(
    images?: Array<{ url?: string; medium?: string; small?: string }>
  ): string | undefined {
    if (!images || images.length === 0) return undefined;

    const firstImage = images[0];
    const imageUrl = firstImage.medium || firstImage.small || firstImage.url;

    if (!imageUrl) return undefined;

    return imageUrl.startsWith("https")
      ? imageUrl
      : `https://evefant.com${imageUrl}`;
  }

  /**
   * Clear cache for performance management
   */
  static clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  static getCacheStats() {
    return this.cache.getStats();
  }
}

// Google Product Category mapping for better catalog classification
const GOOGLE_CATEGORY_MAP: Record<string, string> = {
  flores: "Toys & Games > Party & Celebration > Floral Arrangements",
  regalos: "Arts & Entertainment > Hobbies & Creative Arts > Crafts",
  decoracion: "Home & Garden > Decor",
  plantas: "Home & Garden > Plants",
  jardineria: "Home & Garden > Lawn & Garden",
  tecnologia: "Electronics",
  ropa: "Apparel & Accessories",
  hogar: "Home & Garden",
  belleza: "Health & Beauty",
  deportes: "Sporting Goods",
  libros: "Media > Books",
  juguetes: "Toys & Games",
  electrodomesticos: "Home & Garden > Household Appliances",
};

class OptimizedMetaPixelCatalogService {
  private sessionId: string;
  private isReturningVisitor: boolean;
  private deviceType: "mobile" | "tablet" | "desktop";
  private trafficSource: "organic" | "paid" | "email" | "social" | "direct";
  private initialized = false;

  // Performance tracking
  private eventCount = 0;
  private lastEventTime = 0;

  // Backend integration performance metrics
  private backendDataUsageCount = 0;
  private fallbackGenerationCount = 0;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.isReturningVisitor = this.checkReturningVisitor();
    this.deviceType = this.getDeviceType();
    this.trafficSource = this.getTrafficSource();

    // Lazy initialization
    this.initializeAsync();
  }

  /**
   * Async initialization for better performance
   */
  private async initializeAsync(): Promise<void> {
    if (typeof window === "undefined") return;

    // Initialize dataLayer
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    // Mark visitor for future sessions
    try {
      if (!localStorage.getItem("evefant_visitor_flag")) {
        localStorage.setItem("evefant_visitor_flag", "true");
        localStorage.setItem("evefant_first_visit", Date.now().toString());
      }
    } catch (e) {
      console.warn("Could not access localStorage:", e);
    }

    this.initialized = true;
  }

  /**
   * Generate a unique session ID for tracking
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 15)}`;
  }

  /**
   * Check if user is a returning visitor
   */
  private checkReturningVisitor(): boolean {
    if (typeof window === "undefined") return false;

    try {
      return !!localStorage.getItem("evefant_visitor_flag");
    } catch (e) {
      return false;
    }
  }

  /**
   * Get device type based on screen width
   */
  private getDeviceType(): "mobile" | "tablet" | "desktop" {
    if (typeof window === "undefined") return "desktop";

    const width = window.innerWidth;
    if (width < 768) return "mobile";
    if (width < 1024) return "tablet";
    return "desktop";
  }

  /**
   * Get traffic source from referrer or UTM parameters
   */
  private getTrafficSource():
    | "organic"
    | "paid"
    | "email"
    | "social"
    | "direct" {
    if (typeof window === "undefined") return "direct";

    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get("utm_source");
    const utmMedium = urlParams.get("utm_medium");
    const referrer = document.referrer;

    if (utmMedium === "cpc" || utmMedium === "paid") return "paid";
    if (utmMedium === "email") return "email";
    if (
      utmSource?.includes("facebook") ||
      utmSource?.includes("instagram") ||
      referrer.includes("facebook.com") ||
      referrer.includes("instagram.com")
    )
      return "social";

    if (referrer && !referrer.includes(window.location.hostname))
      return "organic";

    return "direct";
  }

  /**
   * Transform product data to enhanced catalog format with caching
   * OPTIMIZED: Uses pre-computed backend metaPixelCatalogData when available
   */
  private transformProductToCatalogData(
    product: CatalogProduct,
    eventContext?: Partial<EnhancedCatalogData>
  ): EnhancedCatalogData {
    // Check for pre-computed backend data first (PERFORMANCE OPTIMIZATION)
    if ((product as any).metaPixelCatalogData) {
      // Parse JSON string from backend to object
      const backendData =
        typeof (product as any).metaPixelCatalogData === "string"
          ? JSON.parse((product as any).metaPixelCatalogData)
          : (product as any).metaPixelCatalogData;

      // Track backend data usage for performance metrics
      this.backendDataUsageCount++;

      // Merge pre-computed backend data with dynamic session context
      const catalogData: EnhancedCatalogData = {
        ...backendData,
        // Override with current session/user context
        session_id: this.sessionId,
        user_data: {
          source: this.trafficSource,
          device_type: this.deviceType,
          is_returning_visitor: this.isReturningVisitor,
        },
        // Merge any event-specific context
        ...eventContext,
      };

      return catalogData;
    }

    // FALLBACK: Generate catalog data from product fields (legacy/backup path)
    this.fallbackGenerationCount++;
    console.warn(
      `No pre-computed metaPixelCatalogData for product ${product.sku}, using fallback generation`
    );

    const regularPrice = ProductDataHelper.getProductPrice(product);
    const discountPrice = ProductDataHelper.getDiscountPrice(product);
    const finalPrice = discountPrice || regularPrice;

    const discountPercentage = discountPrice
      ? Math.round(((regularPrice - discountPrice) / regularPrice) * 100)
      : 0;

    const catalogData: EnhancedCatalogData = {
      content_ids: [String(product.sku)],
      content_type: "product",
      content_name: product.name,
      content_category: ProductDataHelper.getCategory(product),
      value: finalPrice,
      currency: "PEN",
      brand: ProductDataHelper.getBrand(product),
      retailer_item_id: product.sku,
      availability: this.getAvailabilityStatus(
        product.stock,
        product.isFreebie
      ),
      condition: "new",
      image_url: ProductDataHelper.getImageURL(product.images),
      product_url: ProductDataHelper.getProductURL(product),
      description: product.description?.substring(0, 500),
      inventory: product.stock || 0,
      regular_price: regularPrice,
      sale_price: discountPrice || undefined,
      discount_percentage:
        discountPercentage > 0 ? discountPercentage : undefined,
      google_product_category: this.getGoogleProductCategory(
        ProductDataHelper.getCategory(product)
      ),
      product_type: this.getProductType(product),
      custom_labels: this.generateCustomLabels(product),
      gtin: ProductDataHelper.getMPN(product),
      mpn: ProductDataHelper.getMPN(product),
      session_id: this.sessionId,
      user_data: {
        source: this.trafficSource,
        device_type: this.deviceType,
        is_returning_visitor: this.isReturningVisitor,
      },
      ...eventContext,
    };

    return catalogData;
  }

  /**
   * Get availability status based on stock and product type
   */
  private getAvailabilityStatus(
    stock?: number,
    isFreebie?: boolean
  ): "in stock" | "out of stock" | "preorder" | "backorder" {
    // Freebie products are always available since they're digital/free
    if (isFreebie) return "in stock";

    // Traditional stock-based availability for regular products
    if (!stock || stock <= 0) return "out of stock";
    if (stock < 5) return "backorder";
    return "in stock";
  }

  /**
   * Determine product type based on product characteristics
   */
  private getProductType(product: CatalogProduct): string {
    if (product.customizableOptions && product.customizableOptions.length > 0) {
      return "configurable";
    }
    if (product.attributes && product.attributes.length > 0) {
      return "variable";
    }
    return "simple";
  }

  /**
   * Generate custom labels for product classification
   */
  private generateCustomLabels(product: CatalogProduct): string[] {
    const labels: string[] = [];

    const regularPrice = ProductDataHelper.getProductPrice(product);
    const discountPrice = ProductDataHelper.getDiscountPrice(product);

    // Stock level labels
    if (product.stock) {
      if (product.stock > 50) labels.push("high_stock");
      else if (product.stock < 5) labels.push("low_stock");
      else labels.push("normal_stock");
    }

    // Price range labels
    if (regularPrice > 1000) labels.push("premium");
    else if (regularPrice > 500) labels.push("mid_range");
    else labels.push("budget");

    // Discount labels
    if (discountPrice) {
      const discountPercentage = Math.round(
        ((regularPrice - discountPrice) / regularPrice) * 100
      );
      if (discountPercentage >= 50) labels.push("major_discount");
      else if (discountPercentage >= 20) labels.push("sale_item");
      else labels.push("minor_discount");
    }

    // Product type labels
    if (product.customizableOptions?.length) labels.push("customizable");
    if (product.attributes?.length) labels.push("variable_product");

    return labels;
  }

  /**
   * Map product category to Google Merchant Center category taxonomy
   */
  private getGoogleProductCategory(categoryName: string): string {
    const normalizedCategory = categoryName.toLowerCase();
    return GOOGLE_CATEGORY_MAP[normalizedCategory] || "General Merchandise";
  }

  /**
   * Track enhanced product view event - Optimized for Meta's automatic catalog detection
   */
  public trackProductView(
    product: CatalogProduct,
    additionalData?: Record<string, any>
  ): void {
    if (!this.initialized) {
      setTimeout(() => this.trackProductView(product, additionalData), 100);
      return;
    }

    try {
      const catalogData = this.transformProductToCatalogData(
        product,
        additionalData
      );

      // Meta Pixel event for automatic catalog registration
      if (window.fbq) {
        window.fbq("track", "ViewContent", catalogData);
      }

      // Enhanced dataLayer event for comprehensive tracking
      this.pushToDataLayer({
        event: "view_item",
        ecommerce: {
          currency: catalogData.currency,
          value: catalogData.value,
          items: [
            {
              item_id: catalogData.content_ids[0],
              item_name: catalogData.content_name,
              item_category: catalogData.content_category,
              item_brand: catalogData.brand,
              price: catalogData.value,
              quantity: 1,
            },
          ],
        },
        // Additional Meta Pixel catalog data
        catalog_data: catalogData,
      });

      this.trackEvent("product_view");
    } catch (error) {
      console.error("Error tracking product view:", error);
    }
  }

  /**
   * Track category browsing behavior - Batch optimized
   */
  public trackCategoryView(
    categoryName: string,
    products: CatalogProduct[],
    searchFilters?: Record<string, any>
  ): void {
    if (!this.initialized || products.length === 0) return;

    try {
      // Transform products in batch for better performance
      const catalogItems = products.slice(0, 20).map((product) => {
        const catalogData = this.transformProductToCatalogData(product);
        return {
          item_id: catalogData.content_ids[0],
          item_name: catalogData.content_name,
          item_category: catalogData.content_category,
          item_brand: catalogData.brand,
          price: catalogData.value,
          quantity: 1,
        };
      });

      // Meta Pixel event
      if (window.fbq) {
        window.fbq("track", "ViewCategory", {
          content_category: categoryName,
          content_ids: catalogItems.map((item) => item.item_id),
          contents: catalogItems.map((item) => ({
            id: item.item_id,
            quantity: 1,
          })),
        });
      }

      // Enhanced dataLayer event
      this.pushToDataLayer({
        event: "view_item_list",
        ecommerce: {
          item_list_name: categoryName,
          items: catalogItems,
        },
        search_filters: searchFilters,
      });

      this.trackEvent("category_view");
    } catch (error) {
      console.error("Error tracking category view:", error);
    }
  }

  /**
   * Track search behavior with results - Optimized for Meta catalog learning
   */
  public trackSearch(
    searchQuery: string,
    results: CatalogProduct[],
    searchType: "product" | "blog" = "product"
  ): void {
    if (!this.initialized) return;

    try {
      if (searchType === "product" && results.length > 0) {
        const searchResults = results.slice(0, 10).map((product) => {
          const catalogData = this.transformProductToCatalogData(product, {
            search_string: searchQuery,
          });
          return catalogData;
        });

        // Meta Pixel search event for catalog optimization
        if (window.fbq) {
          window.fbq("track", "Search", {
            search_string: searchQuery,
            content_ids: searchResults.map((item) => item.content_ids[0]),
            contents: searchResults.map((item) => ({
              id: item.content_ids[0],
              quantity: 1,
            })),
          });
        }

        // Enhanced dataLayer event
        this.pushToDataLayer({
          event: "search",
          search_term: searchQuery,
          search_results_count: results.length,
          ecommerce: {
            items: searchResults.map((item) => ({
              item_id: item.content_ids[0],
              item_name: item.content_name,
              item_category: item.content_category,
              item_brand: item.brand,
              price: item.value,
            })),
          },
        });
      }

      this.trackEvent("search");
    } catch (error) {
      console.error("Error tracking search:", error);
    }
  }

  /**
   * Track enhanced add to cart event - Critical for Meta catalog optimization
   */
  public trackAddToCart(
    product: CatalogProduct,
    quantity: number = 1,
    customizations?: any[],
    groupingAttributes?: any[]
  ): void {
    if (!this.initialized) return;

    try {
      const catalogData = this.transformProductToCatalogData(product, {
        num_items: quantity,
      });

      // Meta Pixel add to cart event
      if (window.fbq) {
        window.fbq("track", "AddToCart", {
          content_ids: catalogData.content_ids,
          content_type: catalogData.content_type,
          content_name: catalogData.content_name,
          content_category: catalogData.content_category,
          value: catalogData.value * quantity,
          currency: catalogData.currency,
          contents: [
            {
              id: catalogData.content_ids[0],
              quantity: quantity,
              item_price: catalogData.value,
            },
          ],
        });
      }

      // Enhanced dataLayer event
      this.pushToDataLayer({
        event: "add_to_cart",
        ecommerce: {
          currency: catalogData.currency,
          value: catalogData.value * quantity,
          items: [
            {
              item_id: catalogData.content_ids[0],
              item_name: catalogData.content_name,
              item_category: catalogData.content_category,
              item_brand: catalogData.brand,
              price: catalogData.value,
              quantity: quantity,
            },
          ],
        },
        catalog_data: catalogData,
        customizations: customizations,
        grouping_attributes: groupingAttributes,
      });

      this.trackEvent("add_to_cart");
    } catch (error) {
      console.error("Error tracking add to cart:", error);
    }
  }

  /**
   * Track purchase with enhanced catalog data - Essential for Meta catalog optimization
   */
  public trackPurchase(
    transactionId: string,
    products: Array<CatalogProduct & { quantity: number }>,
    totalValue: number,
    shippingCost?: number,
    taxAmount?: number,
    couponCode?: string
  ): void {
    if (!this.initialized || products.length === 0) return;

    try {
      // Transform all products for purchase tracking
      const catalogItems = products.map((product) =>
        this.transformProductToCatalogData(product)
      );

      // Meta Pixel purchase event
      if (window.fbq) {
        window.fbq("track", "Purchase", {
          value: totalValue,
          currency: "PEN",
          content_ids: catalogItems.map((item) => item.content_ids[0]),
          content_type: "product",
          contents: products.map((product, index) => ({
            id: catalogItems[index].content_ids[0],
            quantity: product.quantity,
            item_price: catalogItems[index].value,
          })),
        });
      }

      // Enhanced dataLayer event
      this.pushToDataLayer({
        event: "purchase",
        transaction_id: transactionId,
        ecommerce: {
          transaction_id: transactionId,
          value: totalValue,
          currency: "PEN",
          shipping: shippingCost,
          tax: taxAmount,
          coupon: couponCode,
          items: products.map((product, index) => ({
            item_id: catalogItems[index].content_ids[0],
            item_name: catalogItems[index].content_name,
            item_category: catalogItems[index].content_category,
            item_brand: catalogItems[index].brand,
            price: catalogItems[index].value,
            quantity: product.quantity,
          })),
        },
        catalog_items: catalogItems,
      });

      this.trackEvent("purchase");
    } catch (error) {
      console.error("Error tracking purchase:", error);
    }
  }

  /**
   * Batch track multiple products for catalog sync - Performance optimized
   */
  public batchTrackCatalogSync(products: CatalogProduct[]): void {
    if (!this.initialized || products.length === 0) return;

    try {
      // Process in chunks to avoid performance issues
      const chunkSize = 50;
      for (let i = 0; i < products.length; i += chunkSize) {
        const chunk = products.slice(i, i + chunkSize);

        setTimeout(() => {
          const catalogItems = chunk.map((product) =>
            this.transformProductToCatalogData(product)
          );

          // Send batch catalog data to Meta Pixel for automatic detection
          this.pushToDataLayer({
            event: "catalog_batch_sync",
            catalog_items: catalogItems,
            batch_size: chunk.length,
            total_products: products.length,
          });

          console.log(
            `📦 Synced catalog batch: ${chunk.length} products (${
              i + chunk.length
            }/${products.length})`
          );
        }, (i / chunkSize) * 100); // Stagger requests
      }

      this.trackEvent("catalog_sync");
    } catch (error) {
      console.error("Error in batch catalog sync:", error);
    }
  }

  /**
   * Push data to dataLayer with error handling and performance tracking
   */
  private pushToDataLayer(data: any): void {
    if (typeof window === "undefined" || !window.dataLayer) return;

    try {
      window.dataLayer.push({
        ...data,
        timestamp: Date.now(),
        session_id: this.sessionId,
        event_sequence: ++this.eventCount,
      });
    } catch (error) {
      console.error("Error pushing to dataLayer:", error);
    }
  }

  /**
   * Track service performance and usage
   */
  private trackEvent(eventType: string): void {
    const now = Date.now();
    const timeSinceLastEvent = this.lastEventTime
      ? now - this.lastEventTime
      : 0;

    this.lastEventTime = now;

    // Log performance in development
    if (process.env.NODE_ENV === "development") {
      console.log(
        `📊 Meta Pixel Event: ${eventType} (${timeSinceLastEvent}ms since last event)`
      );
    }
  }

  /**
   * Enhanced catalog data validation with detailed error reporting
   */
  public validateCatalogData(product: CatalogProduct): {
    isValid: boolean;
    errors: Array<{ field: string; issue: string; suggestion?: string }>;
  } {
    const errors: Array<{ field: string; issue: string; suggestion?: string }> =
      [];

    // Required fields validation
    if (!product.sku) {
      errors.push({
        field: "sku",
        issue: "SKU is required for catalog tracking",
        suggestion: "Ensure each product has a unique SKU",
      });
    }

    if (!product.name) {
      errors.push({
        field: "name",
        issue: "Product name is required",
        suggestion: "Add a descriptive product name",
      });
    }

    // Price validation
    const price = ProductDataHelper.getProductPrice(product);
    if (!price || price <= 0) {
      errors.push({
        field: "price",
        issue: "Valid price is required",
        suggestion: "Ensure product has price.regPrice or priceValue > 0",
      });
    }

    // Image validation
    const imageUrl = ProductDataHelper.getImageURL(product.images);
    if (!imageUrl) {
      errors.push({
        field: "images",
        issue: "Product image is highly recommended for catalog performance",
        suggestion: "Add at least one product image",
      });
    }

    // Category validation
    const category = ProductDataHelper.getCategory(product);
    if (category === "General") {
      errors.push({
        field: "category",
        issue:
          "Specific category recommended for better catalog classification",
        suggestion:
          "Add product to a specific category for better Meta Pixel performance",
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Batch validate an array of catalog products - Performance optimized
   */
  public validateCatalogBatch(products: CatalogProduct[]): {
    validCount: number;
    invalidCount: number;
    results: Array<{
      product: CatalogProduct;
      isValid: boolean;
      errors: Array<{ field: string; issue: string; suggestion?: string }>;
    }>;
  } {
    const results = products.map((product) => {
      const validation = this.validateCatalogData(product);
      return {
        product,
        isValid: validation.isValid,
        errors: validation.errors,
      };
    });

    const validCount = results.filter((r) => r.isValid).length;
    const invalidCount = results.length - validCount;

    return {
      validCount,
      invalidCount,
      results,
    };
  }

  /**
   * Get service performance statistics including backend integration metrics
   */
  public getPerformanceStats(): {
    sessionId: string;
    isReturningVisitor: boolean;
    deviceType: string;
    trafficSource: string;
    eventCount: number;
    cacheStats: any;
    initialized: boolean;
    backendDataUsageCount: number;
    fallbackGenerationCount: number;
    totalTransformations: number;
    backendUsagePercentage: number;
  } {
    const totalTransformations =
      this.backendDataUsageCount + this.fallbackGenerationCount;
    const backendUsagePercentage =
      totalTransformations > 0
        ? Math.round((this.backendDataUsageCount / totalTransformations) * 100)
        : 0;

    return {
      sessionId: this.sessionId,
      isReturningVisitor: this.isReturningVisitor,
      deviceType: this.deviceType,
      trafficSource: this.trafficSource,
      eventCount: this.eventCount,
      cacheStats: ProductDataHelper.getCacheStats(),
      initialized: this.initialized,
      backendDataUsageCount: this.backendDataUsageCount,
      fallbackGenerationCount: this.fallbackGenerationCount,
      totalTransformations,
      backendUsagePercentage,
    };
  }

  /**
   * Clear all caches for memory management
   */
  public clearCaches(): void {
    ProductDataHelper.clearCache();
    console.log("🧹 Meta Pixel Catalog Service caches cleared");
  }

  /**
   * Reset performance counters (useful for testing and monitoring)
   */
  public resetPerformanceStats(): void {
    this.backendDataUsageCount = 0;
    this.fallbackGenerationCount = 0;
    this.eventCount = 0;
    this.lastEventTime = 0;
    console.log("📊 Meta Pixel Catalog Service performance stats reset");
  }

  /**
   * Track blog search behavior
   */
  public trackBlogSearch(
    searchQuery: string,
    resultsCount: number,
    source: string = "blog"
  ): void {
    if (!this.initialized) return;

    try {
      // Meta Pixel search event
      if (window.fbq) {
        window.fbq("track", "Search", {
          search_string: searchQuery,
          content_category: "blog",
          source: source,
        });
      }

      // Enhanced dataLayer event
      this.pushToDataLayer({
        event: "blog_search",
        search_term: searchQuery,
        search_results_count: resultsCount,
        search_source: source,
        session_id: this.sessionId,
      });

      this.trackEvent("blog_search");
    } catch (error) {
      console.error("Error tracking blog search:", error);
    }
  }

  /**
   * Track newsletter subscription
   */
  public trackNewsletterSubscription(
    email: string,
    source: string = "newsletter",
    location: string = "unknown"
  ): void {
    if (!this.initialized) return;

    try {
      // Meta Pixel subscription event
      if (window.fbq) {
        window.fbq("track", "Subscribe", {
          content_category: "newsletter",
          source: source,
          location: location,
        });
      }

      // Enhanced dataLayer event
      this.pushToDataLayer({
        event: "newsletter_subscription",
        subscription_source: source,
        subscription_location: location,
        session_id: this.sessionId,
      });

      this.trackEvent("newsletter_subscription");
    } catch (error) {
      console.error("Error tracking newsletter subscription:", error);
    }
  }

  /**
   * Track blog article view
   */
  public trackBlogView(
    articleId: string,
    articleTitle: string,
    category: string,
    author?: string
  ): void {
    if (!this.initialized) return;

    try {
      // Meta Pixel content view event
      if (window.fbq) {
        window.fbq("track", "ViewContent", {
          content_ids: [articleId],
          content_type: "blog_article",
          content_name: articleTitle,
          content_category: category,
        });
      }

      // Enhanced dataLayer event
      this.pushToDataLayer({
        event: "blog_view",
        article_id: articleId,
        article_title: articleTitle,
        article_category: category,
        article_author: author,
        session_id: this.sessionId,
      });

      this.trackEvent("blog_view");
    } catch (error) {
      console.error("Error tracking blog view:", error);
    }
  }

  /**
   * Track blog read completion
   */
  public trackBlogReadCompletion(
    articleId: string,
    articleTitle: string,
    readPercentage: number,
    timeSpent: number
  ): void {
    if (!this.initialized) return;

    try {
      // Meta Pixel custom event
      if (window.fbq) {
        window.fbq("trackCustom", "BlogReadCompletion", {
          content_ids: [articleId],
          content_name: articleTitle,
          read_percentage: readPercentage,
          time_spent: timeSpent,
        });
      }

      // Enhanced dataLayer event
      this.pushToDataLayer({
        event: "blog_read_completion",
        article_id: articleId,
        article_title: articleTitle,
        read_percentage: readPercentage,
        time_spent_seconds: timeSpent,
        session_id: this.sessionId,
      });

      this.trackEvent("blog_read_completion");
    } catch (error) {
      console.error("Error tracking blog read completion:", error);
    }
  }

  /**
   * Track form submission
   */
  public trackFormSubmission(
    formName: string,
    formId: string,
    success: boolean,
    formData?: Record<string, any>
  ): void {
    if (!this.initialized) return;

    try {
      // Meta Pixel form submission event
      if (window.fbq) {
        window.fbq("track", success ? "CompleteRegistration" : "Lead", {
          content_name: formName,
          content_ids: [formId],
          status: success ? "completed" : "failed",
          source: formData?.form_type || "unknown",
        });
      }

      // Enhanced dataLayer event
      this.pushToDataLayer({
        event: success ? "form_submit_success" : "form_submit_error",
        form_name: formName,
        form_id: formId,
        form_data: formData,
        session_id: this.sessionId,
      });

      this.trackEvent("form_submission");
    } catch (error) {
      console.error("Error tracking form submission:", error);
    }
  }

  /**
   * Track product customization
   */
  public trackProductCustomization(
    product: CatalogProduct,
    optionName: string,
    optionValue: string,
    stepOrType: string | number = "option_selection"
  ): void {
    if (!this.initialized) return;

    try {
      const catalogData = this.transformProductToCatalogData(product);

      // Convert step number to meaningful customization type
      const customizationType =
        typeof stepOrType === "number"
          ? `customization_step_${stepOrType}`
          : stepOrType;

      // Meta Pixel custom event
      if (window.fbq) {
        window.fbq("trackCustom", "ProductCustomization", {
          content_ids: catalogData.content_ids,
          content_name: catalogData.content_name,
          content_category: catalogData.content_category,
          customization_type: customizationType,
          option_name: optionName,
          option_value: optionValue,
          step: typeof stepOrType === "number" ? stepOrType : undefined,
        });
      }

      // Enhanced dataLayer event
      this.pushToDataLayer({
        event: "product_customization",
        ecommerce: {
          currency: catalogData.currency,
          value: catalogData.value,
          items: [
            {
              item_id: catalogData.content_ids[0],
              item_name: catalogData.content_name,
              item_category: catalogData.content_category,
              item_brand: catalogData.brand,
              price: catalogData.value,
              quantity: 1,
            },
          ],
        },
        customization_type: customizationType,
        customization_step:
          typeof stepOrType === "number" ? stepOrType : undefined,
        option_name: optionName,
        option_value: optionValue,
        session_id: this.sessionId,
      });

      this.trackEvent("product_customization");
    } catch (error) {
      console.error("Error tracking product customization:", error);
    }
  }

  /**
   * Initialize catalog sync for automatic Meta Pixel detection
   */
  public initializeCatalogSync(): void {
    if (typeof window === "undefined") return;

    // Send initial catalog detection signal to Meta Pixel
    this.pushToDataLayer({
      event: "catalog_initialization",
      service_version: "2.0.0",
      features: [
        "automatic_catalog_detection",
        "enhanced_product_data",
        "batch_processing",
        "intelligent_caching",
        "performance_optimization",
        "backend_integration",
      ],
      session_id: this.sessionId,
    });

    console.log(
      "🚀 Meta Pixel Catalog Service initialized for automatic detection"
    );
  }
}

// Create and export singleton instance with performance optimization
export const metaPixelCatalogService = new OptimizedMetaPixelCatalogService();

// Initialize catalog sync on service creation
metaPixelCatalogService.initializeCatalogSync();

export default metaPixelCatalogService;
